import { useState } from "react";
import { useScopedOrganizationId } from "@/contexts/WorkspaceOrgContext";
import { DealTypesProductEditor, DealTypesSaleEditor } from "@/components/ops/DealTypesEditors";
import { useDealTypes } from "@/components/ops/useDealTypes";

const tabBtn = (active: boolean) =>
  `rounded-md px-3 py-1.5 text-sm font-medium ${
    active
      ? "bg-slate-800 text-white dark:bg-zinc-200 dark:text-zinc-900"
      : "border border-gray-300 text-gray-800 hover:bg-gray-50 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800"
  }`;

/**
 * Ops → Deal Types (DT-1): product types + sale-type variants + slots.
 * Live instances stay in Ops → Deals. See docs/planning/deal_types_mode.md.
 */
export function DealTypesPanel() {
  const organizationId = useScopedOrganizationId();
  const vm = useDealTypes(organizationId);
  const [newName, setNewName] = useState("");

  if (!organizationId) {
    return (
      <div className="w-full text-left space-y-3">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Deal Types</h2>
        <p className="text-sm text-gray-500 dark:text-zinc-400">Select a workspace organization first.</p>
      </div>
    );
  }

  return (
    <div data-app-panel="DealTypes" className="w-full text-left space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Deal Types</h2>
          <p className="mt-1 max-w-3xl text-sm text-gray-600 dark:text-zinc-300">
            Author <strong>sales-facing</strong> product types (Shed sale, Steel) and{" "}
            <strong>sale types</strong> (Cash, RTO, Finance). Paperwork lives on each layer;
            product plus sale combine into the deal's required set. Instantiating a customer deal
            stays in <strong>Deals</strong>.
          </p>
        </div>
        <button
          type="button"
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-zinc-600"
          onClick={() => void vm.reloadCatalog()}
          disabled={vm.loading}
        >
          {vm.loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Deal type layers">
        <button
          type="button"
          role="tab"
          aria-selected={vm.slider === "product"}
          className={tabBtn(vm.slider === "product")}
          onClick={() => vm.setSlider("product")}
        >
          Product types ({vm.productTypes.length})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={vm.slider === "sale"}
          className={tabBtn(vm.slider === "sale")}
          onClick={() => vm.setSlider("sale")}
        >
          Sale types ({vm.saleTypes.length})
        </button>
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-sm text-gray-700 dark:text-zinc-300">
          New {vm.slider === "product" ? "product" : "sale"} type
          <input
            className="mt-1 block w-64 rounded border border-gray-300 px-2 py-1.5 dark:border-zinc-600 dark:bg-zinc-900"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={vm.slider === "product" ? "Shed sale" : "Cash"}
          />
        </label>
        <button
          type="button"
          className="rounded-md bg-slate-800 px-3 py-1.5 text-sm text-white dark:bg-zinc-200 dark:text-zinc-900"
          onClick={() => {
            const n = newName.trim();
            if (!n) return;
            void (vm.slider === "product" ? vm.createProduct(n) : vm.createSale(n)).then(() =>
              setNewName(""),
            );
          }}
        >
          Create
        </button>
      </div>

      {vm.msg ? (
        <p className="text-sm text-amber-700 dark:text-amber-300" role="status">
          {vm.msg}
        </p>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="min-w-0 space-y-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">
            {vm.slider === "product" ? "Product types" : "Sale types"}
          </h3>
          {vm.slider === "product" ? (
            <TypeList
              rows={vm.productTypes}
              selectedId={vm.selectedProductId}
              onSelect={vm.setSelectedProductId}
              empty="No product types yet — create Shed sale or Steel structure."
            />
          ) : (
            <TypeList
              rows={vm.saleTypes}
              selectedId={vm.selectedSaleId}
              onSelect={vm.setSelectedSaleId}
              empty="No sale types yet — create Cash, RTO, or Finance variants."
            />
          )}
        </section>

        <section className="min-w-0">
          {vm.slider === "product" && vm.selectedProduct ? (
            <DealTypesProductEditor
              key={vm.selectedProduct.id}
              product={vm.selectedProduct}
              saleTypes={vm.saleTypes}
              linkedSaleIds={vm.linkedSaleIdsForProduct}
              slots={vm.slots}
              onSave={vm.saveProduct}
              onDelete={vm.removeProduct}
              onSaveLinks={vm.saveLinks}
              onAddSlot={vm.addSlot}
              onRemoveSlot={vm.removeSlot}
            />
          ) : null}
          {vm.slider === "sale" && vm.selectedSale ? (
            <DealTypesSaleEditor
              key={vm.selectedSale.id}
              sale={vm.selectedSale}
              slots={vm.slots}
              onSave={vm.saveSale}
              onDelete={vm.removeSale}
              onAddSlot={vm.addSlot}
              onRemoveSlot={vm.removeSlot}
            />
          ) : null}
          {vm.slider === "product" && !vm.selectedProduct ? (
            <p className="text-sm text-gray-500 dark:text-zinc-400">Select or create a product type.</p>
          ) : null}
          {vm.slider === "sale" && !vm.selectedSale ? (
            <p className="text-sm text-gray-500 dark:text-zinc-400">Select or create a sale type.</p>
          ) : null}
        </section>
      </div>
    </div>
  );
}

function TypeList(props: {
  rows: { id: string; display_name: string; type_key: string; is_active: boolean }[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  empty: string;
}) {
  if (!props.rows.length) {
    return <p className="text-sm text-gray-500 dark:text-zinc-400">{props.empty}</p>;
  }
  return (
    <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-zinc-700 dark:border-zinc-700">
      {props.rows.map((row) => {
        const active = props.selectedId === row.id;
        return (
          <li key={row.id}>
            <button
              type="button"
              className={`flex w-full flex-col gap-0.5 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-zinc-800/60 ${
                active ? "bg-slate-50 dark:bg-zinc-800" : ""
              }`}
              onClick={() => props.onSelect(row.id)}
            >
              <span className="font-medium text-gray-900 dark:text-zinc-100">{row.display_name}</span>
              <span className="text-xs text-gray-500 dark:text-zinc-400">
                {row.type_key}
                {row.is_active ? "" : " · inactive"}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
